import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { Download } from 'lucide-react';
import { dateLabel } from '@adisys/shared';
import { api, downloadCsv } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { errorText, inr } from '../../lib/crm';
import { PageHeader } from '../../components/AppShell';
import { Button, Card, CardHeader, EmptyState, ErrorState, TableSkeleton, cx, useToast } from '../../components/ui';
import { AXIS_PROPS, BAR_RADIUS, CHART, ChartTooltip, GRID_PROPS } from '../../components/charts';

interface Col { key: string; label: string; type?: 'money' | 'percent' | 'number' | 'text' | 'date' }

const REPORTS = [
  { key: 'pipeline', label: 'Pipeline by stage', blurb: 'Projects, value and time spent at each stage.' },
  { key: 'sales-performance', label: 'Sales performance', blurb: 'Enquiries handled, conversion and order value by executive.' },
  { key: 'lead-sources', label: 'Lead sources', blurb: 'Where enquiries come from and which convert.' },
  { key: 'receivables', label: 'Receivables', blurb: 'What each customer owes and how late it is.' },
  { key: 'vendors', label: 'Vendor performance', blurb: 'Purchase volume and delivery speed by vendor.' },
  { key: 'margin', label: 'Margin by project', blurb: 'Confidential: revenue, cost and margin per approved price.' },
];

function cell(col: Col, v: any) {
  if (v === null || v === undefined || v === '') return '—';
  switch (col.type) {
    case 'money': return inr(v);
    case 'percent': return `${Number(v)}%`;
    case 'number': return Number(v).toLocaleString('en-IN');
    case 'date': return dateLabel(v);
    default: return String(v);
  }
}

/** Which column to chart for the reports where a picture helps. */
const CHARTS: Record<string, { x: string; y: string; name: string }> = {
  pipeline: { x: 'label', y: 'value', name: 'Pipeline value' },
  'sales-performance': { x: 'name', y: 'orderValue', name: 'Order value' },
};

export function CrmReportsPage() {
  const { can } = useAuth();
  const toast = useToast();
  const [name, setName] = useState('pipeline');
  const [busy, setBusy] = useState(false);
  const available = REPORTS.filter((r) => r.key !== 'margin' || can('crm.cost.view'));
  const current = available.find((r) => r.key === name) ?? available[0]!;

  const q = useQuery({ queryKey: ['crm', 'reports', current.key], queryFn: () => api.get(`/crm/reports/${current.key}`) });
  const columns: Col[] = q.data?.columns ?? [];
  const rows: any[] = q.data?.rows ?? [];
  const chart = CHARTS[current.key];

  const download = async () => {
    setBusy(true);
    try { await downloadCsv(`/crm/reports/${current.key}`, {}, `crm-${current.key}.csv`); }
    catch (e) { toast.error('Download failed', errorText(e)); }
    finally { setBusy(false); }
  };

  return (
    <>
      <PageHeader title="CRM reports" description="Pipeline, sales, collections and supply, with every figure exportable."
        actions={<Button icon={<Download className="h-4 w-4" />} loading={busy} onClick={() => void download()}>Download CSV</Button>} />
      <div className="grid gap-5 p-4 sm:p-6 lg:grid-cols-[15rem_1fr]">
        <nav aria-label="Reports" className="flex gap-1 overflow-x-auto lg:flex-col">
          {available.map((r) => (
            <button key={r.key} onClick={() => setName(r.key)} aria-current={r.key === current.key}
              className={cx('whitespace-nowrap rounded-lg px-3 py-2 text-left text-sm font-medium transition-colors',
                r.key === current.key ? 'bg-ink-900 text-white' : 'text-ink-600 hover:bg-ink-100')}>
              {r.label}
            </button>))}
        </nav>

        <div className="min-w-0 space-y-5">
          {chart && rows.length > 0 && (
            <Card>
              <CardHeader title={current.label} subtitle={current.blurb} />
              <div className="h-64" role="img" aria-label={`${chart.name} by ${chart.x}`}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={rows}>
                    <CartesianGrid {...GRID_PROPS} />
                    <XAxis dataKey={chart.x} {...AXIS_PROPS} />
                    <YAxis {...AXIS_PROPS} width={56} tickFormatter={(v) => inr(v, true)} />
                    <Tooltip content={<ChartTooltip formatter={(v: number) => inr(v)} />} />
                    <Bar dataKey={chart.y} name={chart.name} fill={CHART.assigned} radius={BAR_RADIUS} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Card>)}

          <Card padded={false} className="overflow-hidden">
            <div className="p-5 pb-3"><CardHeader title={q.data?.title ?? current.label} subtitle={chart ? undefined : current.blurb} /></div>
            {q.isLoading ? <TableSkeleton cols={5} />
              : q.isError ? <ErrorState error={q.error} onRetry={() => q.refetch()} />
              : rows.length === 0 ? <EmptyState title="Nothing to report yet" description="Figures appear as work moves through the pipeline." />
              : (
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[560px] text-left text-sm">
                    <thead><tr className="border-y border-line bg-sunken/60">
                      {columns.map((c) => (
                        <th key={c.key} scope="col" className={cx('px-4 py-2.5 text-sm font-bold text-ink-600',
                          ['money', 'percent', 'number'].includes(c.type ?? '') && 'text-right')}>{c.label}</th>))}
                    </tr></thead>
                    <tbody className="divide-y divide-line">
                      {rows.map((r, i) => (
                        <tr key={i} className="hover:bg-ink-50">
                          {columns.map((c) => (
                            <td key={c.key} className={cx('px-4 py-2.5 text-ink-800',
                              ['money', 'percent', 'number'].includes(c.type ?? '') && 'tabular text-right')}>{cell(c, r[c.key])}</td>))}
                        </tr>))}
                    </tbody>
                  </table>
                </div>)}
          </Card>
        </div>
      </div>
    </>
  );
}
